/**
 * Enterprise System Filters Component
 *
 * Provides comprehensive filtering options for enterprise systems:
 * - Text search (name, vendor)
 * - Category filter
 * - Vendor filter
 * - Status filter
 * - Quality score range
 * - Active filter badges
 */

import React, { useState, useCallback } from 'react';
import { Search, X, Filter, SlidersHorizontal } from 'lucide-react';

export interface FilterState {
  searchText: string;
  categories: string[];
  vendors: string[];
  statuses: string[];
  qualityRange: [number, number];
}

interface EnterpriseSystemFiltersProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  availableCategories?: string[];
  availableVendors?: string[];
  availableStatuses?: string[];
}

const DEFAULT_CATEGORIES = ['ERP', 'CRM', 'SCM', 'HCM', 'FIN', 'BI', 'PLM', 'MES', 'WMS'];
const DEFAULT_STATUSES = ['pending', 'researching', 'quality_review', 'published', 'archived'];

const EnterpriseSystemFilters: React.FC<EnterpriseSystemFiltersProps> = ({
  filters,
  onFiltersChange,
  availableCategories = DEFAULT_CATEGORIES,
  availableVendors = [],
  availableStatuses = DEFAULT_STATUSES,
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  /**
   * Handle search text change with debouncing
   */
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onFiltersChange({
        ...filters,
        searchText: e.target.value,
      });
    },
    [filters, onFiltersChange]
  );

  /**
   * Toggle category filter
   */
  const toggleCategory = useCallback(
    (category: string) => {
      const newCategories = filters.categories.includes(category)
        ? filters.categories.filter((c) => c !== category)
        : [...filters.categories, category];

      onFiltersChange({
        ...filters,
        categories: newCategories,
      });
    },
    [filters, onFiltersChange]
  );

  /**
   * Toggle vendor filter
   */
  const toggleVendor = useCallback(
    (vendor: string) => {
      const newVendors = filters.vendors.includes(vendor)
        ? filters.vendors.filter((v) => v !== vendor)
        : [...filters.vendors, vendor];

      onFiltersChange({
        ...filters,
        vendors: newVendors,
      });
    },
    [filters, onFiltersChange]
  );

  /**
   * Toggle status filter
   */
  const toggleStatus = useCallback(
    (status: string) => {
      const newStatuses = filters.statuses.includes(status)
        ? filters.statuses.filter((s) => s !== status)
        : [...filters.statuses, status];

      onFiltersChange({
        ...filters,
        statuses: newStatuses,
      });
    },
    [filters, onFiltersChange]
  );

  /**
   * Handle quality range change
   */
  const handleQualityRangeChange = useCallback(
    (index: 0 | 1, value: number) => {
      const newRange: [number, number] = [...filters.qualityRange];
      newRange[index] = value;

      // Ensure min <= max
      if (newRange[0] > newRange[1]) {
        if (index === 0) {
          newRange[1] = newRange[0];
        } else {
          newRange[0] = newRange[1];
        }
      }

      onFiltersChange({
        ...filters,
        qualityRange: newRange,
      });
    },
    [filters, onFiltersChange]
  );

  /**
   * Clear all filters
   */
  const clearAllFilters = useCallback(() => {
    onFiltersChange({
      searchText: '',
      categories: [],
      vendors: [],
      statuses: [],
      qualityRange: [0, 100],
    });
  }, [onFiltersChange]);

  /**
   * Remove individual filter
   */
  const removeFilter = useCallback(
    (type: 'category' | 'vendor' | 'status', value: string) => {
      if (type === 'category') {
        toggleCategory(value);
      } else if (type === 'vendor') {
        toggleVendor(value);
      } else if (type === 'status') {
        toggleStatus(value);
      }
    },
    [toggleCategory, toggleVendor, toggleStatus]
  );

  /**
   * Count active filters
   */
  const activeFilterCount =
    filters.categories.length +
    filters.vendors.length +
    filters.statuses.length +
    (filters.qualityRange[0] > 0 || filters.qualityRange[1] < 100 ? 1 : 0);

  /**
   * Format status label
   */
  const formatStatus = (status: string): string => {
    return status
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="space-y-4">
      {/* Search and Quick Filters */}
      <div className="flex items-center gap-4">
        {/* Search Input */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
          <input
            type="text"
            placeholder="Search by name or vendor..."
            value={filters.searchText}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          {filters.searchText && (
            <button
              onClick={() =>
                onFiltersChange({ ...filters, searchText: '' })
              }
              className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Advanced Filters Toggle */}
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
            showAdvanced || activeFilterCount > 0
              ? 'bg-primary-50 border-primary-300 text-primary-700'
              : 'bg-white border-neutral-300 text-neutral-700 hover:bg-neutral-50'
          }`}
        >
          <SlidersHorizontal className="w-4 h-4" />
          <span className="font-medium">Filters</span>
          {activeFilterCount > 0 && (
            <span className="ml-1 px-2 py-0.5 bg-primary-600 text-white text-xs rounded-full">
              {activeFilterCount}
            </span>
          )}
        </button>

        {/* Clear All Button */}
        {activeFilterCount > 0 && (
          <button
            onClick={clearAllFilters}
            className="flex items-center gap-2 px-4 py-2 text-sm text-neutral-600 hover:text-neutral-900 transition-colors"
          >
            <X className="w-4 h-4" />
            Clear All
          </button>
        )}
      </div>

      {/* Active Filter Badges */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap gap-2">
          {filters.categories.map((category) => (
            <span
              key={category}
              className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
            >
              Category: {category}
              <button
                onClick={() => removeFilter('category', category)}
                className="hover:text-blue-900"
                aria-label={`Remove ${category} filter`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}

          {filters.vendors.map((vendor) => (
            <span
              key={vendor}
              className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm"
            >
              Vendor: {vendor}
              <button
                onClick={() => removeFilter('vendor', vendor)}
                className="hover:text-purple-900"
                aria-label={`Remove ${vendor} filter`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}

          {filters.statuses.map((status) => (
            <span
              key={status}
              className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
            >
              Status: {formatStatus(status)}
              <button
                onClick={() => removeFilter('status', status)}
                className="hover:text-green-900"
                aria-label={`Remove ${status} filter`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}

          {(filters.qualityRange[0] > 0 || filters.qualityRange[1] < 100) && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
              Quality: {filters.qualityRange[0]}% - {filters.qualityRange[1]}%
              <button
                onClick={() =>
                  onFiltersChange({ ...filters, qualityRange: [0, 100] })
                }
                className="hover:text-yellow-900"
                aria-label="Remove quality range filter"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          )}
        </div>
      )}

      {/* Advanced Filters Panel */}
      {showAdvanced && (
        <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-6 space-y-6">
          {/* Category Filter */}
          <div>
            <label className="block text-sm font-semibold text-neutral-900 mb-3">
              Category
            </label>
            <div className="flex flex-wrap gap-2">
              {availableCategories.map((category) => (
                <button
                  key={category}
                  onClick={() => toggleCategory(category)}
                  className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    filters.categories.includes(category)
                      ? 'bg-primary-600 border-primary-600 text-white'
                      : 'bg-white border-neutral-300 text-neutral-700 hover:bg-neutral-50'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Vendor Filter */}
          {availableVendors.length > 0 && (
            <div>
              <label className="block text-sm font-semibold text-neutral-900 mb-3">
                Vendor
              </label>
              <div className="flex flex-wrap gap-2">
                {availableVendors.map((vendor) => (
                  <button
                    key={vendor}
                    onClick={() => toggleVendor(vendor)}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      filters.vendors.includes(vendor)
                        ? 'bg-secondary-600 border-secondary-600 text-white'
                        : 'bg-white border-neutral-300 text-neutral-700 hover:bg-neutral-50'
                    }`}
                  >
                    {vendor}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-semibold text-neutral-900 mb-3">
              Status
            </label>
            <div className="flex flex-wrap gap-2">
              {availableStatuses.map((status) => (
                <button
                  key={status}
                  onClick={() => toggleStatus(status)}
                  className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    filters.statuses.includes(status)
                      ? 'bg-green-600 border-green-600 text-white'
                      : 'bg-white border-neutral-300 text-neutral-700 hover:bg-neutral-50'
                  }`}
                >
                  {formatStatus(status)}
                </button>
              ))}
            </div>
          </div>

          {/* Quality Score Range */}
          <div>
            <label className="block text-sm font-semibold text-neutral-900 mb-3">
              Quality Score Range
            </label>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <label className="block text-xs text-neutral-600 mb-1">
                    Minimum
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={filters.qualityRange[0]}
                    onChange={(e) =>
                      handleQualityRangeChange(0, parseInt(e.target.value))
                    }
                    className="w-full"
                  />
                  <div className="text-sm font-medium text-neutral-900 mt-1">
                    {filters.qualityRange[0]}%
                  </div>
                </div>

                <div className="flex-1">
                  <label className="block text-xs text-neutral-600 mb-1">
                    Maximum
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={filters.qualityRange[1]}
                    onChange={(e) =>
                      handleQualityRangeChange(1, parseInt(e.target.value))
                    }
                    className="w-full"
                  />
                  <div className="text-sm font-medium text-neutral-900 mt-1">
                    {filters.qualityRange[1]}%
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between text-xs text-neutral-500">
                <span>0%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnterpriseSystemFilters;
